import React, {  useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
} from "chart.js";
// @ts-ignore
import ChartDataLabels from "chartjs-plugin-datalabels";
import BarChartShimmer from "components/reportComponents/Charts/BarChartShimmer";
import CustomDropdown from "components/common/customDropdown";
import DownloadReport from "components/reportComponents/DownloadReports";
import SwitchableBox from "components/reportComponents/SwitchableBox";
import "./style.scss";
import { formatNumberByK } from "utils";
import ReportNotFound from "../../ReportsNotFound";
import NewTable from "components/reportComponents/NewTable";
import { useWindowSize } from "hooks/useWindowSize";
import { dataLabelsForCharts, tableRowLimitLargeDevice, tableRowLimitSmallDevice } from "constants/reportConstants";

// Register required components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

interface BarChartProps {
    kpiTitle: string;
    dataList: Record<string, any>[]; // Accepts any dataset
    loader: boolean;
    error: boolean;
    xKey: string; // Key for X-axis labels
    yKey: string; // Key for Y-axis values
    xLabel: string;
    yLabel: string;
    extraKeys?: { key: string; label: string }[]; // Additional keys for tooltips (e.g., "orders")
    barColor?: string; // Custom bar color
    title?: string; // Chart title
    xPrefix?: string; // Prefix for X-axis labels
    xSuffix?: string; // Suffix for X-axis labels
    yPrefix?: string; // Prefix for Y-axis values
    ySuffix?: string; // Suffix for Y-axis values
    tooltipStyles?: {
        backgroundColor?: string;
        borderColor?: string;
        titleColor?: string;
        bodyColor?: string;
    };
    formatAmount?: boolean;
    titleFontSize?: number;
    bodyFontSize?: number;
    barPercentage?: number;
    categoryPercentage?: number;
    showChartFilter: boolean,
    handleChartFilter?: (selectedValueForChart: string, kpiTitle: string) => void,
    getToggledValueInParentPage?: (activeTextForChart: string, kpiTitle: string) => void;
    switchableTextOne?: string;
    switchableTextTwo?: string;
    showSwitchable: boolean;
    isYAxisQuantity?: boolean;
    isSwitchActive?: boolean;
    setIsSwitchActive?: () => void;
    chartFilterOptions?: { value: string, label: string }[]
    reRender?:boolean;
    maxRotation?: boolean;
    minRotation?: boolean;
    enableXAxisValueInTooltip?: boolean;
    enableYAxisValueInTooltip?: boolean;
    isMobileView?: boolean;
    currentPage?: number;
    setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
    headerData?: { key: string, label: string }[];
    showHeader?: boolean;
}


const ReusableBarChart: React.FC<BarChartProps> = ({
    kpiTitle,
    dataList = [],
    loader,
    error,
    xKey,
    yKey,
    xLabel,
    yLabel,
    extraKeys = [],
    barColor = "#1F77B4",
    title = "Sales Chart",
    xPrefix = "",
    xSuffix = "",
    yPrefix = "",
    ySuffix = "",
    formatAmount = false,
    tooltipStyles = {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        borderColor: "#2196F3",
        titleColor: "#333",
        bodyColor: "#333",
    },
    titleFontSize = 14,
    bodyFontSize = 14,
    barPercentage = 0.6,
    categoryPercentage = 0.6,
    handleChartFilter,
    showChartFilter = true,
    getToggledValueInParentPage,
    switchableTextOne = "Option 1",
    switchableTextTwo = "Option 2",
    showSwitchable = "true",
    isYAxisQuantity = "false",
    isSwitchActive = false,
    setIsSwitchActive = () => { },
    chartFilterOptions = [],
    reRender =false,
    maxRotation=true,
    minRotation=true,
    enableXAxisValueInTooltip=true,
    enableYAxisValueInTooltip=true,
    isMobileView=false,
    currentPage = 1,
    setCurrentPage,
    headerData = [],
    showHeader = true,
}) => {

    const { width } = useWindowSize();
    
    const reusableBarChartRef = useRef<HTMLDivElement>(null)
    const [activeTextForSwitchableBox, setActiveTextForSwitchableBox] = useState<string>(switchableTextOne);
    const [selectedFilter, setSelectedFilter] = useState<{ value: string; label: string; icon?: React.ReactNode } | undefined>(chartFilterOptions?.[0]);
    useEffect(() => {
        // TODO: handle selectedFilter from Parent
        setSelectedFilter(chartFilterOptions?.[0])
    }, [reRender])

    const data = {
        labels: dataList?.length ? Array.from(
            new Set(dataList?.filter((item) => !!item[yKey])?.map((item) => `${xPrefix}${item[xKey]}${xSuffix}`))
        ) : [],
        datasets: [
            {
                label: title,
                data: (dataList || [])?.filter((item) => !!item[yKey])?.map((item) => ({
                    x: `${xPrefix}${item[xKey]}${xSuffix}`,
                    y: Number(item[yKey] || 0),
                    ...extraKeys?.reduce((acc, { key }) => ({ ...acc, [key]: item[key] }), {}),
                })),
                backgroundColor: barColor,
                barPercentage,
                categoryPercentage,
            },
        ],
    };

    // Calculation of max value for Y-axis padding
    const maxValue = Math.max(...dataList.map(item => Number(item[yKey] || 0)));
    // Ensuring paddedMax is at least 10 for small values
    const paddedMax = maxValue > 0 ? Math.max(maxValue * 1.1, 10) : 10;

    const options: ChartOptions<"bar"> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: true,
                callbacks: {
                    title: () => "",
                    label: (tooltipItem: any) => {
                        const dataPoint = tooltipItem.raw;
                        const yValue = isYAxisQuantity ? dataPoint?.y : dataPoint?.y?.toFixed(2);

                        const lines: string[] = [];

                        if (enableXAxisValueInTooltip) {
                            lines.push(`${xLabel}: ${dataPoint?.x}`);
                        }

                        if (enableYAxisValueInTooltip) {
                            lines.push(`${yLabel}: ${yPrefix}${yValue}${ySuffix}`);
                        }

                        extraKeys?.forEach(({ key, label }) => {
                            if (dataPoint[key] !== undefined) {
                                lines.push(`${label}: ${dataPoint[key]}`);
                            }
                        });

                        return lines;
                    },
                },
                backgroundColor: tooltipStyles.backgroundColor,
                borderColor: tooltipStyles.borderColor,
                titleColor: tooltipStyles.titleColor,
                bodyColor: tooltipStyles.bodyColor,
                titleFont: { size: titleFontSize, family: "sans-serif" },
                bodyFont: { size: bodyFontSize, family: "sans-serif" },
                borderWidth: 1,
                padding: 15,
                displayColors: false,
                caretSize: 0,
            },
            datalabels: dataLabelsForCharts,
        },
        scales: {
            x: { 
                grid: { display: false } ,
                ticks: isMobileView ? {display: false} : {
                maxRotation: maxRotation ? 90 : 0,
                minRotation: minRotation ? 45 : 0,
                callback: (value: any, index: number, ticks: any) => {
                  const label = data.labels?.[index] || "";
                  const maxLength =20; // customize this length
                    return label.length > maxLength ? label.slice(0, maxLength) + "..." : label; 
                },
              }
        },
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => `${yPrefix}${formatAmount ? formatNumberByK(value) : value}${ySuffix}`,
                    font: {
                        family: "sans-serif",
                    }
                },
                max : paddedMax?.toFixed(0),
            },
        },
    };

    const handleChartFilterParent = (selectedValue: { label: string, value: string }) => {
        setSelectedFilter(selectedValue)
        if (handleChartFilter) {
            handleChartFilter(selectedValue?.value, kpiTitle);
        }
    }

    const handleToggleSwitchParent = () => {
        setIsSwitchActive();
        setActiveTextForSwitchableBox((prev) => {
            const newValue = prev === switchableTextOne ? switchableTextTwo : switchableTextOne;
            if (getToggledValueInParentPage) {
                getToggledValueInParentPage(newValue, kpiTitle);
            }
            return newValue;
        });
    }


    if (loader) return <BarChartShimmer />;

    const dataWithoutItemId = dataList?.map(item => {
        const { itemId, ...rest } = item; // Use destructuring to extract itemId and the rest of the properties
        return rest; // Return the object containing the remaining properties
    });



    return (
        <div className='report-product-charts-container' ref={reusableBarChartRef}>
            {showHeader ? <div className='report-product-heading-download-container'>
                <div className="title-switchable-box-container">
                    <h2 className="report-product-chart-heading">{title || "Chart title"}</h2>
                    {showSwitchable &&
                        <SwitchableBox
                            textOne={switchableTextOne}
                            textTwo={switchableTextTwo}
                            isActive={isSwitchActive}
                            toggleSwitch={handleToggleSwitchParent}
                        />
                    }
                </div>
                <div className="chart-filter-download-report-container" style={{ justifyContent: !showChartFilter ? "flex-end" : "" }}>
                    {showChartFilter &&
                        <div className="chart-filter-container">
                            <CustomDropdown
                                value={selectedFilter}
                                options={chartFilterOptions}
                                onSelect={handleChartFilterParent}
                                placeholder="Select Date"
                                className="table-date-dropdown"
                                disabled={false}
                            />
                        </div>
                    }
                    <DownloadReport kpiTitle={kpiTitle} tableData={dataWithoutItemId} downloadRef={reusableBarChartRef} />
                </div>
            </div> : <></>}

            <div className='sm-report-product-heading-download-container'>
                <div className="sm-title-chart-filter-download-report-container">
                    <h2 className="sm-report-product-chart-heading">{title || "Chart title"}</h2>
                    {/* <div className="sm-chart-filter-container" style={{ justifyContent: !showChartFilter ? "flex-end" : "" }}> */}
                    {showChartFilter &&
                        <div className="sm-chart-filter-container">
                            <CustomDropdown
                                value={selectedFilter}
                                options={chartFilterOptions}
                                onSelect={handleChartFilterParent}
                                placeholder="Select Date"
                                className="table-date-dropdown"
                                disabled={false}
                            />
                        </div>
                    }
                    {/* </div> */}
                    <DownloadReport kpiTitle={kpiTitle} tableData={dataWithoutItemId} downloadRef={reusableBarChartRef} />
                </div>
                <div className="sm-switchable-box">
                    {showSwitchable &&
                        <SwitchableBox
                            textOne={switchableTextOne}
                            textTwo={switchableTextTwo}
                            isActive={isSwitchActive}
                            toggleSwitch={handleToggleSwitchParent}
                        />
                    }
                </div>
            </div>

            {dataList?.length === 0 ? (
                <ReportNotFound errorType={"reportNotFound"} />
            ) : (error ? <ReportNotFound errorType={"reportNotFound"} /> :
                    <React.Fragment>
                        <div 
                        className="desktop-view"
                        >
                            <div style={{ width: "100%", height: "500px" }}>
                                <Bar data={data} options={options} />
                            </div>
                        </div>
                        {/* commented out for pre-prod release */}
                        <div className="mobile-view">
                            <NewTable
                                showTableHeader={false}
                                kpiTitle={`${kpiTitle} (${dataList?.length ?? 0})`}
                                headerData={headerData}
                                tableData={dataList}
                                totalPages={Math.ceil((dataList?.length ?? 0) / tableRowLimitSmallDevice)}
                                currentPage={currentPage}
                                onPageChange={setCurrentPage}
                                rowsPerPage={tableRowLimitSmallDevice}
                                loader={loader}
                                totalElements={dataList?.length}
                            />
                        </div>
                    </React.Fragment>
            )}
        </div>
    )
};

export default ReusableBarChart;
